import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../../config/api";
import {
  Plane,
  Search,
  Loader2,
  RefreshCw,
  ExternalLink,
  Package,
  MapPin,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  Bell,
  Info,
  Building,
} from "lucide-react";

// Common airline prefixes (first 3 digits of AWB)
const COMMON_AIRLINES = [
  { code: "020", name: "Lufthansa Cargo" },
  { code: "057", name: "Air France Cargo" },
  { code: "074", name: "KLM Cargo" },
  { code: "081", name: "Qantas" },
  { code: "098", name: "Asiana Cargo" },
  { code: "105", name: "Austrian Airlines" },
  { code: "117", name: "Emirates SkyCargo" },
  { code: "125", name: "British Airways" },
  { code: "131", name: "Japan Airlines" },
  { code: "157", name: "Qatar Airways Cargo" },
  { code: "160", name: "Cathay Pacific Cargo" },
  { code: "176", name: "Emirates" },
  { code: "180", name: "Korean Air Cargo" },
  { code: "205", name: "All Nippon Airways" },
  { code: "217", name: "Thai Airways" },
  { code: "235", name: "Turkish Airlines" },
  { code: "257", name: "Finnair Cargo" },
  { code: "297", name: "EVA Air Cargo" },
  { code: "618", name: "Singapore Airlines Cargo" },
  { code: "695", name: "China Airlines Cargo" },
  { code: "706", name: "Etihad Cargo" },
  { code: "988", name: "Cargolux" },
];

export default function AirCargoTrackingContent() {
  const { token } = useAuth();
  const [awbNumber, setAwbNumber] = useState("");
  const [tracking, setTracking] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [loadingWatchlist, setLoadingWatchlist] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState("");
  const [apiStatus, setApiStatus] = useState(null);

  // Fetch API status
  const fetchApiStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/air-tracking/api-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApiStatus(response.data);
    } catch (error) {
      console.error("Error fetching API status:", error);
    }
  }, [token]);

  // Fetch air cargo watchlist
  const fetchWatchlist = useCallback(async () => {
    setLoadingWatchlist(true);
    try {
      const response = await axios.get(
        `${API_URL}/air-tracking/watchlist`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWatchlist(response.data.items || []);
    } catch (error) {
      console.error("Error fetching air watchlist:", error);
    } finally {
      setLoadingWatchlist(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchWatchlist();
      fetchApiStatus();
    }
  }, [token, fetchWatchlist, fetchApiStatus]);

  // Format AWB number to standard format (123-12345678)
  const formatAwbNumber = (input) => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 11) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 11)}`;
  };

  // Extract airline code from AWB
  const getAirlineFromAwb = (awb) => {
    const prefix = awb.replace(/-/g, "").slice(0, 3);
    return COMMON_AIRLINES.find(a => a.code === prefix);
  };

  // Handle AWB input change
  const handleAwbChange = (e) => {
    const formatted = formatAwbNumber(e.target.value);
    setAwbNumber(formatted);
    
    // Auto-detect airline
    if (formatted.length >= 3) {
      const airline = getAirlineFromAwb(formatted);
      if (airline) {
        setSelectedAirline(airline.code);
      }
    }
  };

  // Track AWB via track-trace.com (opens in new window)
  const handleTrackDirect = () => {
    if (!awbNumber || awbNumber.length < 11) {
      toast.error("Please enter a valid AWB number (format: 123-12345678)");
      return;
    }
    
    const awbClean = awbNumber.replace(/-/g, "");
    window.open(`https://www.track-trace.com/aircargo?search=${awbClean}`, "_blank");
  };

  // Track AWB and add to watchlist
  const handleTrackAndWatch = async () => {
    if (!awbNumber || awbNumber.length < 11) {
      toast.error("Please enter a valid AWB number (format: 123-12345678)");
      return;
    }

    setTracking(true);
    try {
      const response = await axios.post(
        `${API_URL}/air-tracking/track`,
        { 
          awb_number: awbNumber,
          add_to_watchlist: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTrackingResult(response.data);
      
      if (response.data.milestones?.length > 0) {
        toast.success(`Tracked ${response.data.airline_name}: ${response.data.milestones.length} events found`);
      } else {
        toast.success("AWB added to watchlist for monitoring");
      }
      
      fetchWatchlist();
    } catch (error) {
      console.error("Tracking error:", error);
      toast.error(error.response?.data?.detail || "Failed to track AWB");
    } finally {
      setTracking(false);
    }
  };

  // Refresh single AWB
  const handleRefreshSingle = async (itemId) => {
    try {
      const response = await axios.post(
        `${API_URL}/air-tracking/watchlist/refresh/${itemId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.milestones?.length > 0) {
        toast.success(`Updated: ${response.data.milestones.length} events`);
      } else {
        toast.success("AWB refreshed");
      }
      
      fetchWatchlist();
    } catch (error) {
      toast.error("Failed to refresh");
    }
  };

  // Remove from watchlist
  const handleRemoveFromWatchlist = async (id) => {
    try {
      await axios.delete(
        `${API_URL}/air-tracking/watchlist/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Removed from watchlist");
      fetchWatchlist();
    } catch (error) {
      toast.error("Failed to remove from watchlist");
    }
  };

  // Refresh watchlist status
  const handleRefreshWatchlist = async () => {
    setLoadingWatchlist(true);
    try {
      await axios.post(
        `${API_URL}/air-tracking/watchlist/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Watchlist refreshed");
      fetchWatchlist();
    } catch (error) {
      toast.error("Failed to refresh watchlist");
    } finally {
      setLoadingWatchlist(false);
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusLower = (status || "").toLowerCase();
    if (statusLower.includes("delivered") || statusLower.includes("arrived")) {
      return <Badge className="bg-green-100 text-green-800 border-green-200">Delivered</Badge>;
    }
    if (statusLower.includes("transit") || statusLower.includes("departed")) {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">In Transit</Badge>;
    }
    if (statusLower.includes("customs") || statusLower.includes("hold")) {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Customs</Badge>;
    }
    if (statusLower.includes("booked") || statusLower.includes("received")) {
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Booked</Badge>;
    }
    return <Badge variant="outline">{status || "Unknown"}</Badge>;
  };

  return (
    <div className="max-w-[1800px] mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Plane className="h-6 w-6 text-blue-600" />
            Air Cargo Tracking
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track air waybills (AWB) across 244+ airlines worldwide
          </p>
        </div>
      </div>

      {/* Search Card */}
      <Card className="border-2 border-blue-100">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            Track Air Waybill (AWB)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-sm text-slate-600 mb-2 block">
                AWB Number (format: 123-12345678)
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., 176-12345678"
                  value={awbNumber}
                  onChange={handleAwbChange}
                  className="text-lg font-mono"
                  maxLength={12}
                  data-testid="awb-input"
                />
                <Button
                  onClick={handleTrackDirect}
                  variant="outline"
                  className="shrink-0"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Track Online
                </Button>
                <Button
                  onClick={handleTrackAndWatch}
                  disabled={tracking || awbNumber.length < 11}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700"
                  data-testid="track-awb-btn"
                >
                  {tracking ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add to Watchlist
                </Button>
              </div>
            </div>
            
            <div>
              <Label className="text-sm text-slate-600 mb-2 block">
                Airline (auto-detected)
              </Label>
              <Select value={selectedAirline} onValueChange={setSelectedAirline}>
                <SelectTrigger>
                  <SelectValue placeholder="Auto-detect from AWB" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_AIRLINES.map((airline) => (
                    <SelectItem key={airline.code} value={airline.code}>
                      {airline.code} - {airline.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Info box */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <strong>AWB Format:</strong> The first 3 digits identify the airline (e.g., 176 = Emirates, 235 = Turkish Airlines, 618 = Singapore Airlines).
                The remaining 8 digits are the shipment number. Use <strong>"Track Online"</strong> to view live tracking data on track-trace.com,
                or <strong>"Add to Watchlist"</strong> to save the AWB for quick access.
              </div>
            </div>
          </div>

          {/* API Status */}
          {apiStatus && (
            <div className={`mt-3 p-3 rounded-lg border ${apiStatus.tracking_enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-2">
                {apiStatus.tracking_enabled ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                )}
                <div className="text-sm">
                  <strong className={apiStatus.tracking_enabled ? 'text-green-800' : 'text-amber-800'}>
                    {apiStatus.tracking_enabled ? 'Automated Tracking Active' : 'Manual Tracking Mode'}
                  </strong>
                  <p className={`mt-0.5 ${apiStatus.tracking_enabled ? 'text-green-700' : 'text-amber-700'}`}>
                    {apiStatus.message}
                  </p>
                  {!apiStatus.tracking_enabled && (
                    <p className="mt-1 text-amber-600 text-xs">
                      For automated milestone updates, configure <strong>SEARATES_API_KEY</strong> (recommended - 447+ airlines) in the backend environment.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plane className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{watchlist.length}</p>
                <p className="text-xs text-muted-foreground">Tracking</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {watchlist.filter(w => w.status?.toLowerCase().includes("delivered")).length}
                </p>
                <p className="text-xs text-muted-foreground">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {watchlist.filter(w => w.status?.toLowerCase().includes("transit")).length}
                </p>
                <p className="text-xs text-muted-foreground">In Transit</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">244</p>
                <p className="text-xs text-muted-foreground">Airlines Supported</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Watchlist */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              Air Cargo Watchlist
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor your air shipments for status updates
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefreshWatchlist} disabled={loadingWatchlist}>
            {loadingWatchlist ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh All
          </Button>
        </CardHeader>
        <CardContent>
          {loadingWatchlist && watchlist.length === 0 ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
              <p className="text-sm text-muted-foreground mt-2">Loading watchlist...</p>
            </div>
          ) : watchlist.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <Plane className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <h3 className="font-semibold text-slate-600">No AWBs in watchlist</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add an AWB number above to start tracking
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {watchlist.map((item) => (
                <div key={item.id} className="border rounded-lg p-4 hover:border-blue-200 transition-colors">
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="font-mono font-bold text-lg">{item.awb_number}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <Plane className="h-3 w-3 text-blue-500" />
                          <span className="text-sm text-muted-foreground">
                            {item.airline_name || getAirlineFromAwb(item.awb_number)?.name || "Unknown"}
                          </span>
                        </div>
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                    <div className="flex items-center gap-2">
                      {item.pf_number ? (
                        <a
                          href={`/position-folders?search=${item.pf_number}`}
                          className="text-blue-600 hover:underline text-sm bg-blue-50 px-2 py-1 rounded"
                        >
                          {item.pf_number}
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-1 rounded">
                          No PF linked
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRefreshSingle(item.id)}
                        title="Refresh tracking"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(item.tracking_url || `https://www.track-trace.com/aircargo?search=${item.awb_number.replace(/-/g, "")}`, "_blank")}
                        title="Track on airline site"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFromWatchlist(item.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Remove from watchlist"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Route Info */}
                  {(item.origin || item.destination) && (
                    <div className="flex items-center gap-2 text-sm mb-3 text-slate-600">
                      <MapPin className="h-4 w-4 text-green-500" />
                      <span className="font-medium">{item.origin || "?"}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-medium">{item.destination || "?"}</span>
                    </div>
                  )}
                  
                  {/* Milestones Timeline */}
                  {item.milestones && item.milestones.length > 0 ? (
                    <div className="bg-slate-50 rounded-lg p-3 mt-2">
                      <p className="text-xs font-semibold text-slate-500 mb-2">
                        TRACKING EVENTS ({item.milestones.length})
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {item.milestones.slice(0, 5).map((milestone, idx) => (
                          <div key={idx} className="flex items-start gap-3 text-sm">
                            <div className={`w-2 h-2 rounded-full mt-1.5 ${idx === 0 ? 'bg-green-500' : 'bg-slate-300'}`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{milestone.description || milestone.event_type}</span>
                                {milestone.flight_number && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                    {milestone.flight_number}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {milestone.location && <span>{milestone.location} • </span>}
                                {milestone.event_datetime && new Date(milestone.event_datetime).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))}
                        {item.milestones.length > 5 && (
                          <p className="text-xs text-muted-foreground text-center pt-2">
                            +{item.milestones.length - 5} more events
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground mt-2 p-3 bg-slate-50 rounded-lg text-center">
                      No tracking events yet. Click refresh to fetch latest status.
                    </div>
                  )}
                  
                  {/* Last Update */}
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last checked: {item.last_update ? new Date(item.last_update).toLocaleString() : "Never"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Popular Airlines Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Popular Airline AWB Prefixes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {COMMON_AIRLINES.slice(0, 12).map((airline) => (
              <div key={airline.code} className="p-2 bg-slate-50 rounded text-xs">
                <span className="font-mono font-bold text-blue-600">{airline.code}</span>
                <span className="text-slate-600 ml-1">- {airline.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
